import React, { useEffect, useRef } from 'react';
import AIBadge from '@/components/shared/AIBadge';
import { QualityFeedback } from '@/components/shared/QualityFeedback';
import Spinner from '@/components/common/Spinner';
import cn from '@/utils/cn';
import type { GradeBand } from '../constants';
import useAiReadingLevelFeedback from './useAiReadingLevelFeedback';

interface CompareColumnProps {
  bodyHtml?: string;
  fontSize?: string;
  gradeBand?: GradeBand;
  isLoading: boolean;
  label: string;
  labelClassName?: string;
  llmCompletionId?: number | null;
  locale?: string;
  sectionId?: number;
  showAIBadge?: boolean;
}

const SELECTORS_TO_STRIP = [
  '.vocab-block-bkg',
  '.vocab-box',
  '.vocab-terms',
  '.vocab-box-header',
  '.loc-grid',
  '.loc-list',
  '.glossary_list',
  '[contenteditable]',
  'img',
  'video',
  'iframe',
  '.captions',
  '.illustration',
  '.illustration_div',
  '.wrap-image',
  '.lesson-opener-title',
  '.essential-question',
  '.intro-image-column',
  '.image-column',
];

const ColumnBody = ({ bodyHtml, isLoading }: Pick<CompareColumnProps, 'bodyHtml' | 'isLoading'>) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;

    SELECTORS_TO_STRIP.forEach((selector) => {
      contentRef.current!.querySelectorAll(selector).forEach(el => el.remove());
    });

    // Cap all direct-child theme wrappers (.G1, .Global, .EL-SCI, etc.) and nested
    // .text-column to the column width; keeps the elements so their font-size rules cascade.
    contentRef.current.querySelectorAll<HTMLElement>(':scope > *, .text-column').forEach((el) => {
      el.style.setProperty('width', '100%', 'important');
      el.style.setProperty('max-width', '100%');
    });
  }, [bodyHtml]);

  if (isLoading) {
    return <Spinner className="tw-flex tw-items-center tw-justify-center tw-py-12" />;
  }

  if (bodyHtml) {
    return <div ref={contentRef} className="fr-view" dangerouslySetInnerHTML={{ __html: bodyHtml }} />;
  }

  return <p className="tw-text-med-dark-grey tw-italic">No content available.</p>;
};

const ReadingLevelFeedback = ({ gradeBand, llmCompletionId, locale, sectionId }: {
  gradeBand: GradeBand;
  llmCompletionId: number | null;
  locale: string;
  sectionId: number;
}) => {
  const feedback = useAiReadingLevelFeedback(sectionId, gradeBand, locale, llmCompletionId);

  return (
    <QualityFeedback
      className="tw-ml-auto tw-mr-11"
      disabled={feedback.isSubmitting}
      rating={feedback.rating}
      onRate={feedback.submitFeedback}
    />
  );
};

const CompareColumn = ({
  bodyHtml,
  fontSize,
  gradeBand,
  isLoading,
  label,
  labelClassName,
  llmCompletionId,
  locale,
  sectionId,
  showAIBadge = false,
}: CompareColumnProps) => (
  <div className="tw-flex tw-flex-1 tw-flex-col tw-min-w-0 tw-overflow-hidden">
    <div className="tw-shrink-0 tw-px-4">
      <div className="tw-flex tw-items-center tw-gap-2 tw-pt-4 tw-pb-2">
        <span className={cn('tw-text-base tw-font-bold', labelClassName)}>
          {label}
        </span>
        {showAIBadge && <AIBadge />}
        {showAIBadge && gradeBand && locale && sectionId && (
          <ReadingLevelFeedback
            gradeBand={gradeBand}
            llmCompletionId={llmCompletionId ?? null}
            locale={locale}
            sectionId={sectionId}
          />
        )}
      </div>
      <hr className="tw-border-0 tw-border-t tw-border-solid tw-border-light-grey !tw-m-0" />
    </div>
    <div
      className={cn(
        'tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-px-6 tw-pb-0 tw-pt-4',
        '[&_p]:!tw-pr-0 tw-break-words',
        'section_text_content',
        fontSize,
      )}
    >
      <ColumnBody bodyHtml={bodyHtml} isLoading={isLoading} />
    </div>
  </div>
);

export default CompareColumn;
